import React, { useCallback, useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { ALL_EXPEDIENT_STATUSES, expedientStatusLabel } from '@claimup/shared/constants/expedient';
import api, { getErrorMessage } from '../../../../services/api';
import { useCompanies, nombreDeEmpresa } from '../../useCompanies';
import {
  SUPUESTOS,
  MODOS,
  CANALES,
  DIAS,
  PAISES,
  IDIOMAS,
  TIPOS_DE_TAREA,
  NOMBRES_DE_CAMPO,
  INVARIANTES,
  SI_FALTA,
} from './forma';
import { Bloque, Interruptor, Numero, Casillas, ListaOrdenada, Anadir } from './controles';

/**
 * El panel de mando del policía.
 *
 * 🔴 **Todo lo que el despachador usa para decidir se cambia desde aquí.** No
 * hay reglas en el código que esta pantalla no pueda mover, y ésa es la
 * condición que Edgar puso el 14/09/2026 después de que fallara tres veces por
 * el mismo motivo: una regla en una constante del backend que nadie podía ver
 * paraba la cola, y el síntoma era «no entra nada» — que parece prudencia, no
 * avería.
 *
 * Lo que queda en el backend son los valores de arranque, para cuando el
 * documento de configuración todavía no existe. Eso no es una regla: es qué pasa
 * antes de que nadie haya dicho nada.
 *
 * La pantalla también cuenta CÓMO ESTÁ LA COSA, no solo cómo debería estar: al
 * lado de cada regla va cuántas tareas está reteniendo ahora mismo. Una regla
 * sin su número se discute a ciegas.
 */

const estadosComoOpciones = () =>
  (ALL_EXPEDIENT_STATUSES || []).map((e) => ({ id: e, nombre: expedientStatusLabel(e) }));

const ReglasDelPolicia = () => {
  const [config, setConfig] = useState(null);
  const [borrador, setBorrador] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  // `client,company`: un expediente de recobro cuelga de un `client` y uno de AR
  // de una `company`, y hay que poder aparcar cualquiera de los dos.
  const clientes = useCompanies('client,company');
  const nombreCliente = (id) => nombreDeEmpresa(clientes, id) || String(id);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/wa-dispatch/settings');
      setConfig(data.data);
      setBorrador({ ...SI_FALTA, ...data.data.efectiva });
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (campo, valor) => {
    setBorrador((b) => ({ ...b, [campo]: valor }));
    setGuardado(false);
  };
  /** Para los campos que son un objeto: cambia una clave sin pisar el resto. */
  const cambiarEn = (campo, clave, valor) =>
    cambiar(campo, { ...(borrador[campo] || {}), [clave]: valor });

  const guardar = async () => {
    setGuardando(true);
    try {
      // Se manda EXACTAMENTE la lista del contrato compartido. Un control cuyo
      // campo no esté en él se editaría en pantalla y no se guardaría, sin decir
      // nada — y el test del backend tampoco lo vigilaría.
      const cuerpo = {};
      for (const campo of NOMBRES_DE_CAMPO) {
        if (borrador[campo] !== undefined) cuerpo[campo] = borrador[campo];
      }
      const { data } = await api.put('/wa-dispatch/settings', cuerpo);
      setConfig(data.data);
      setBorrador({ ...SI_FALTA, ...data.data.efectiva });
      setGuardado(true);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!borrador) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 px-6 py-8'>
        <p className='text-sm text-slate-500'>{error || 'Cargando las reglas del policía…'}</p>
      </div>
    );
  }

  const entrada = borrador.entrada || SI_FALTA.entrada;
  const ventana = borrador.ventana || SI_FALTA.ventana;
  const aparcados = borrador.clientes_aparcados || [];
  const lineas = borrador.lineas || [];
  // Cuántas tareas retiene ahora mismo cada motivo. Lo manda el backend.
  const retiene = config?.retenciones || {};
  const cuantas = (motivo) => (retiene[motivo] ? `${retiene[motivo]} ahora mismo` : null);

  const Marcador = ({ motivo }) => {
    const n = cuantas(motivo);
    if (!n) return null;
    return (
      <span className='ml-2 text-[11px] font-medium text-amber-700 bg-amber-50 rounded-lg px-1.5 py-0.5'>
        retiene {n}
      </span>
    );
  };

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
        <h3 className='text-lg font-medium text-slate-900'>Reglas del policía</h3>
        <p className='text-sm text-slate-500 mt-1'>
          Todo lo que decide si un expediente pasa a revisión. Se aplica sin desplegar, y no hay
          nada en el código que no se pueda cambiar aquí.
        </p>
      </div>

      {/* ───────────────────────── 1 · QUÉ MIRA ───────────────────────── */}
      <Bloque
        titulo='1 · Qué tareas mira siquiera'
        descripcion='Lo que no cumpla esto no se retiene: es que el policía ni lo ve. Es la respuesta a «¿por qué ha entrado este expediente?».'
      >
        <div className='mb-4'>
          <p className='text-xs text-slate-500 mb-1.5'>Tipos de tarea que despacha</p>
          <Casillas
            nombre='Tipos que despacha'
            opciones={TIPOS_DE_TAREA}
            seleccion={entrada.tipos}
            onChange={(v) => cambiar('entrada', { ...entrada, tipos: v })}
          />
        </div>
        <div className='flex flex-wrap gap-6'>
          <Numero
            etiqueta='Caduca el sello a las'
            unidad='horas'
            detalle='Una tarea sellada no se vuelve a mirar. Con 0 el sello no caduca nunca, y entonces conserva para siempre una línea elegida hace días.'
            min={0}
            max={720}
            valor={entrada.caducidad_sello_horas}
            onChange={(v) => cambiar('entrada', { ...entrada, caducidad_sello_horas: v })}
          />
          <Numero
            etiqueta='Reintento de lo retenido'
            unidad='min'
            detalle='Cuánto espera una tarea retenida antes de volver a mirarse.'
            min={1}
            max={1440}
            valor={entrada.reintento_min}
            onChange={(v) => cambiar('entrada', { ...entrada, reintento_min: v })}
          />
          <Numero
            etiqueta='Mira como mucho'
            unidad='candidatas'
            detalle='Las más antiguas primero. Si la cola pasa de aquí, lo retenido por motivos que no cambian ocupa los primeros puestos y tapa lo nuevo.'
            min={50}
            max={5000}
            step={50}
            valor={entrada.tope_candidatas}
            onChange={(v) => cambiar('entrada', { ...entrada, tope_candidatas: v })}
          />
          <Numero
            etiqueta='Sella como mucho'
            unidad='por pasada'
            detalle='El policía pasa cada cinco minutos.'
            min={1}
            max={200}
            valor={entrada.tareas_por_pasada}
            onChange={(v) => cambiar('entrada', { ...entrada, tareas_por_pasada: v })}
          />
        </div>
      </Bloque>

      {/* ───────────────────────── 2 · CUÁNDO ───────────────────────── */}
      <Bloque
        titulo='2 · Cuándo puede despachar'
        descripcion='La ventana de WhatsApp, solo suya. Cambiarla aquí no toca los correos ni las llamadas.'
      >
        <div className='mb-3'>
          <p className='text-xs text-slate-500 mb-1.5'>Días</p>
          <Casillas
            nombre='Días de la ventana'
            opciones={DIAS}
            seleccion={(ventana.dias || []).map(String)}
            onChange={(v) => cambiar('ventana', { ...ventana, dias: v.map(Number) })}
            compacto
          />
        </div>
        <div className='flex flex-wrap gap-6'>
          <Numero
            etiqueta='Desde las'
            unidad='h'
            min={0}
            max={23}
            valor={ventana.inicio}
            onChange={(v) => cambiar('ventana', { ...ventana, inicio: v })}
          />
          <Numero
            etiqueta='Hasta las'
            unidad='h'
            min={1}
            max={24}
            valor={ventana.fin}
            onChange={(v) => cambiar('ventana', { ...ventana, fin: v })}
          />
        </div>
        <p className='text-xs text-slate-400 mt-3'>
          Hora de {ventana.zona}. <Marcador motivo='outside_window' />
        </p>
      </Bloque>

      {/* ───────────────────────── 3 · LOS CUATRO SUPUESTOS ───────────────────────── */}
      <Bloque
        titulo='3 · Los cuatro supuestos'
        descripcion='La diferencia no es quién entra antes: es quién gasta cupo. El cupo protege a un número de parecer un desconocido abordando gente, y donde ya hay conversación no hay nada que proteger.'
      >
        <div className='space-y-4'>
          {SUPUESTOS.map((s, i) => {
            const regla = borrador.supuestos?.[s.id] || {};
            const set = (campo, valor) =>
              cambiarEn('supuestos', s.id, { ...regla, [campo]: valor });
            return (
              <div key={s.id} className='border border-slate-200 rounded-lg p-3.5'>
                <div className='flex flex-wrap items-baseline justify-between gap-2 mb-1'>
                  <p className='text-sm font-medium text-slate-900'>
                    {s.nombre}
                    <Marcador motivo={s.id} />
                  </p>
                  <label className='text-xs text-slate-500'>
                    Orden{' '}
                    <input
                      type='number'
                      aria-label={`${s.nombre}: orden en la cola`}
                      min={1}
                      max={99}
                      value={regla.orden ?? i + 1}
                      onChange={(e) => set('orden', Number(e.target.value))}
                      className='w-14 rounded-lg border-slate-300 text-sm tabular-nums ml-1'
                    />
                  </label>
                </div>
                <p className='text-xs text-slate-500'>{s.cuando}</p>
                <p className='text-xs text-slate-400 mb-2.5'>Riesgo de bloqueo: {s.riesgo}</p>
                <div className='flex flex-wrap gap-2'>
                  {MODOS.map((m) => {
                    const activo = (regla.a_revision || 'con_reglas') === m.id;
                    return (
                      <button
                        key={m.id}
                        type='button'
                        onClick={() => set('a_revision', m.id)}
                        aria-pressed={activo}
                        className={`text-left rounded-lg border px-3 py-2 text-xs flex-1 min-w-[200px] ${
                          activo
                            ? 'border-indigo-400 bg-indigo-50 text-indigo-900'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        <span className='block font-medium'>{m.nombre}</span>
                        <span className='block mt-0.5'>{m.detalle}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Bloque>

      {/* ───────────────────────── 4 · EL ORDEN DE LA COLA ───────────────────────── */}
      <Bloque
        titulo='4 · En qué orden los atiende'
        descripcion='Cuatro escalones, de arriba abajo: la urgencia del estado, el supuesto, el cliente, y a igualdad la que lleva más tiempo esperando.'
      >
        <p className='text-xs font-medium text-slate-700 mb-1'>
          Estados que adelantan a todo, sea de quien sea
        </p>
        <p className='text-xs text-slate-500 mb-2'>
          Va por encima del orden de clientes. Es lo que permite aparcar una cartera sin enterrar
          a sus deudores a punto de pagar.
        </p>
        <Casillas
          nombre='Estados urgentes'
          opciones={estadosComoOpciones()}
          seleccion={borrador.estados_urgentes}
          onChange={(v) => cambiar('estados_urgentes', v)}
          compacto
        />

        <p className='text-xs font-medium text-slate-700 mt-5 mb-1'>Orden de clientes</p>
        <p className='text-xs text-slate-500 mb-2'>
          Para lo que no es urgente. Lo que no esté en la lista va después, no antes.
        </p>
        <ListaOrdenada
          nombre='el orden de clientes'
          elementos={borrador.orden_de_clientes || []}
          nombreDe={nombreCliente}
          onChange={(v) => cambiar('orden_de_clientes', v)}
          vacio='Sin orden: todos van igual, por antigüedad.'
        />
        <Anadir
          etiqueta='Añadir un cliente al orden'
          placeholder='Añadir un cliente al orden…'
          opciones={clientes.map((c) => ({ id: c._id, nombre: nombreCliente(c._id) }))}
          yaPuestos={borrador.orden_de_clientes || []}
          onAdd={(id) => cambiar('orden_de_clientes', [...(borrador.orden_de_clientes || []), id])}
        />
      </Bloque>

      {/* ───────────────────────── 5 · A QUIÉN NO SE LE ESCRIBE ───────────────────────── */}
      <Bloque
        titulo='5 · A quién no se le escribe'
        descripcion='Lo de aquí no es ritmo: es respeto al cliente y al deudor. Se comprueba antes que nada, también en los supuestos que van directos.'
      >
        <p className='text-xs font-medium text-slate-700 mb-1'>
          Clientes aparcados
          <Marcador motivo='cliente_aparcado' />
        </p>
        <p className='text-xs text-slate-500 mb-2'>
          Se acota: a qué supuestos afecta y salvo qué estados. Así un cliente en stand-by deja de
          recibir primeros contactos pero sus Hot siguen corriendo.
        </p>

        {aparcados.length === 0 ? (
          <p className='text-xs text-slate-400'>Ninguno.</p>
        ) : (
          <ul className='space-y-3'>
            {aparcados.map((p, i) => {
              const set = (campo, valor) =>
                cambiar(
                  'clientes_aparcados',
                  aparcados.map((x, j) => (j === i ? { ...x, [campo]: valor } : x))
                );
              return (
                <li key={p.client_id} className='border border-slate-200 rounded-lg p-3'>
                  <div className='flex items-center justify-between gap-2 mb-2'>
                    <span className='text-sm font-medium text-slate-800'>
                      {nombreCliente(p.client_id)}
                    </span>
                    <button
                      type='button'
                      onClick={() =>
                        cambiar('clientes_aparcados', aparcados.filter((_, j) => j !== i))
                      }
                      aria-label={`Dejar de aparcar a ${nombreCliente(p.client_id)}`}
                      className='text-xs text-slate-400 hover:text-red-600'
                    >
                      Quitar
                    </button>
                  </div>
                  <div className='flex flex-wrap gap-x-6 gap-y-3'>
                    <label className='text-xs text-slate-600'>
                      <span className='block text-slate-500 mb-1'>Hasta</span>
                      <input
                        type='date'
                        aria-label={`Fecha de fin del aparcado de ${nombreCliente(p.client_id)}`}
                        value={p.hasta ? String(p.hasta).slice(0, 10) : ''}
                        onChange={(e) => set('hasta', e.target.value || null)}
                        className='rounded-lg border-slate-300 text-sm'
                      />
                      {!p.hasta && (
                        <span className='block text-amber-600 mt-1'>
                          sin caducidad — es la que se olvida
                        </span>
                      )}
                    </label>
                    <div className='text-xs text-slate-600'>
                      <span className='block text-slate-500 mb-1'>Le para estos supuestos</span>
                      <Casillas
                        nombre={`Supuestos aparcados de ${nombreCliente(p.client_id)}`}
                        opciones={SUPUESTOS}
                        seleccion={p.supuestos}
                        onChange={(v) => set('supuestos', v)}
                        compacto
                      />
                    </div>
                    <div className='text-xs text-slate-600'>
                      <span className='block text-slate-500 mb-1'>Salvo si el expediente está</span>
                      <Casillas
                        nombre={`Estados a salvo de ${nombreCliente(p.client_id)}`}
                        opciones={estadosComoOpciones()}
                        seleccion={p.salvo_estados}
                        onChange={(v) => set('salvo_estados', v)}
                        compacto
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <Anadir
          etiqueta='Aparcar un cliente'
          placeholder='Aparcar un cliente…'
          opciones={clientes.map((c) => ({ id: c._id, nombre: nombreCliente(c._id) }))}
          yaPuestos={aparcados.map((p) => p.client_id)}
          onAdd={(id) =>
            cambiar('clientes_aparcados', [
              ...aparcados,
              {
                client_id: id,
                nombre: nombreCliente(id),
                hasta: null,
                // Por defecto para lo de riesgo, no para las conversaciones: es
                // lo que casi siempre se quiere y lo que menos daño hace.
                supuestos: ['primer_contacto', 'repeticion'],
                salvo_estados: borrador.estados_urgentes || [],
              },
            ])
          }
        />

        <div className='mt-6'>
          <Interruptor
            etiqueta='Retener primeros contactos a quien ya ha respondido'
            detalle='Esa tarea usa el texto de presentación, y mandárselo a quien lleva semanas negociando es el peor error de este sistema.'
            checked={borrador.bloquear_primer_contacto_si_ya_hablo !== false}
            onChange={(v) => cambiar('bloquear_primer_contacto_si_ya_hablo', v)}
          />
          <p className='text-xs text-slate-400 mt-1 ml-6'>
            Esta sí se apaga: la alternativa buena —convertir esas tareas a seguimiento, que
            tiene el prompt adecuado— todavía no está hecha.
          </p>
        </div>

        <p className='text-xs font-medium text-slate-700 mt-6 mb-1'>
          Estados que no reciben mensajes en frío
          <Marcador motivo='protected_state' />
        </p>
        <p className='text-xs text-slate-500 mb-2'>
          Frena solo los primeros contactos. Los seguimientos y las respuestas de esos mismos
          expedientes pasan igual.
        </p>
        <Casillas
          nombre='Estados fuera del frío'
          opciones={estadosComoOpciones()}
          seleccion={borrador.estados_fuera_del_frio}
          onChange={(v) => cambiar('estados_fuera_del_frio', v)}
          compacto
        />

        <p className='text-xs font-medium text-slate-700 mt-6 mb-1'>
          Países a los que se escribe
          <Marcador motivo='telefono_invalido' />
        </p>
        <p className='text-xs text-slate-500 mb-2'>
          Un número que no encaje en ninguno se queda fuera: mejor no escribir que escribir a un
          número inventado.
        </p>
        <Casillas
          nombre='Países permitidos'
          opciones={PAISES}
          seleccion={borrador.paises_permitidos}
          onChange={(v) => cambiar('paises_permitidos', v)}
          compacto
        />
      </Bloque>

      {/* ───────────────────────── 6 · POR DÓNDE SALE ───────────────────────── */}
      <Bloque
        titulo='6 · Por qué línea sale'
        descripcion='Y con qué idioma. El orden de canales solo se usa en conversaciones nuevas.'
      >
        <Interruptor
          etiqueta='Emparejar el país de la línea con el del deudor'
          detalle='Un español sale por línea española; un americano, por americana.'
          checked={borrador.emparejar_pais_de_linea !== false}
          onChange={(v) => cambiar('emparejar_pais_de_linea', v)}
        />

        <p className='text-xs font-medium text-slate-700 mt-5 mb-1'>
          Orden de canales para conversaciones nuevas
          <Marcador motivo='sin_linea_con_hueco' />
        </p>
        <ListaOrdenada
          nombre='el orden de canales'
          elementos={borrador.orden_de_canales || []}
          nombreDe={(id) => CANALES.find((c) => c.id === id)?.nombre || id}
          onChange={(v) => cambiar('orden_de_canales', v)}
          vacio='Sin orden: no saldría nada.'
        />
        <Anadir
          etiqueta='Añadir un canal al orden'
          placeholder='Añadir un canal…'
          opciones={CANALES}
          yaPuestos={borrador.orden_de_canales || []}
          onAdd={(id) => cambiar('orden_de_canales', [...(borrador.orden_de_canales || []), id])}
        />

        <p className='text-xs font-medium text-slate-700 mt-5 mb-1'>Idioma del mensaje</p>
        <p className='text-xs text-slate-500 mb-2'>
          Lo marca el cliente, no el país del teléfono.
        </p>
        <label className='text-xs text-slate-600 block mb-2'>
          <span className='block text-slate-500 mb-1'>Por defecto</span>
          <select
            aria-label='Idioma por defecto'
            value={borrador.idioma_por_defecto || 'es'}
            onChange={(e) => cambiar('idioma_por_defecto', e.target.value)}
            className='rounded-lg border-slate-300 text-sm'
          >
            {IDIOMAS.map((i) => (
              <option key={i.id} value={i.id}>
                {i.nombre}
              </option>
            ))}
          </select>
        </label>
        {Object.entries(borrador.idioma_por_cliente || {}).map(([id, idioma]) => (
          <div key={id} className='flex items-center gap-2 text-sm text-slate-700 py-1'>
            <span className='flex-1'>{nombreCliente(id)}</span>
            <select
              aria-label={`Idioma de ${nombreCliente(id)}`}
              value={idioma}
              onChange={(e) => cambiarEn('idioma_por_cliente', id, e.target.value)}
              className='rounded-lg border-slate-300 text-sm'
            >
              {IDIOMAS.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.nombre}
                </option>
              ))}
            </select>
            <button
              type='button'
              aria-label={`Quitar el idioma propio de ${nombreCliente(id)}`}
              onClick={() => {
                const copia = { ...(borrador.idioma_por_cliente || {}) };
                delete copia[id];
                cambiar('idioma_por_cliente', copia);
              }}
              className='text-xs text-slate-400 hover:text-red-600'
            >
              Quitar
            </button>
          </div>
        ))}
        <Anadir
          etiqueta='Dar idioma propio a un cliente'
          placeholder='Dar idioma propio a un cliente…'
          opciones={clientes.map((c) => ({ id: c._id, nombre: nombreCliente(c._id) }))}
          yaPuestos={Object.keys(borrador.idioma_por_cliente || {})}
          onAdd={(id) => cambiarEn('idioma_por_cliente', id, 'en')}
        />
      </Bloque>

      {/* ───────────────────────── 7 · CUÁNTO PUEDE MANDAR ───────────────────────── */}
      <Bloque
        titulo='7 · Cuánto puede mandar cada canal'
        descripcion='El cupo en frío. Solo lo gastan los supuestos marcados como «sigue las reglas».'
      >
        <div className='space-y-5'>
          {CANALES.map((canal) => {
            const c = borrador.canales?.[canal.id] || {};
            const set = (campo, valor) =>
              cambiarEn('canales', canal.id, { ...c, [campo]: valor });
            return (
              <div key={canal.id} className='border border-slate-200 rounded-lg p-3.5'>
                <div className='flex items-center justify-between mb-3'>
                  <span className='text-sm font-medium text-slate-900'>
                    {canal.nombre}{' '}
                    <span className='text-xs text-slate-400 font-normal'>{canal.detalle}</span>
                  </span>
                  <Interruptor
                    etiqueta={`${canal.nombre} encendido`}
                    checked={c.activo !== false}
                    onChange={(v) => set('activo', v)}
                  />
                </div>
                <div className='flex flex-wrap gap-x-6 gap-y-4'>
                  <Numero
                    etiqueta='Frío a partir de las'
                    unidad='h'
                    detalle='Vacío = sin puerta, jornada completa.'
                    min={0}
                    max={23}
                    valor={c.hora_inicio_frio}
                    onChange={(v) => set('hora_inicio_frio', v)}
                  />
                  <Numero
                    etiqueta='Base primeros'
                    min={0}
                    max={100}
                    valor={c.base_nuevos}
                    onChange={(v) => set('base_nuevos', v)}
                  />
                  <Numero
                    etiqueta='Techo primeros'
                    min={0}
                    max={200}
                    valor={c.techo_nuevos}
                    onChange={(v) => set('techo_nuevos', v)}
                  />
                  <Numero
                    etiqueta='Base repeticiones'
                    min={0}
                    max={100}
                    valor={c.base_repeticiones}
                    onChange={(v) => set('base_repeticiones', v)}
                  />
                  <Numero
                    etiqueta='Techo repeticiones'
                    min={0}
                    max={200}
                    valor={c.techo_repeticiones}
                    onChange={(v) => set('techo_repeticiones', v)}
                  />
                  <Numero
                    etiqueta='Respuestas por mensaje extra'
                    detalle='Cuántas respuestas hacen falta para ganar un mensaje más.'
                    min={1}
                    max={20}
                    valor={c.respuestas_por_mensaje_extra}
                    onChange={(v) => set('respuestas_por_mensaje_extra', v)}
                  />
                  <Numero
                    etiqueta='Señales para «caliente»'
                    detalle='Con tantas conversaciones vivas hoy, la línea acelera.'
                    min={1}
                    max={20}
                    valor={c.senales_caliente}
                    onChange={(v) => set('senales_caliente', v)}
                  />
                  <Numero
                    etiqueta='Hueco si está caliente'
                    unidad='min'
                    detalle='Nunca sube el de la ficha, solo lo baja.'
                    min={1}
                    max={240}
                    valor={c.hueco_caliente_min}
                    onChange={(v) => set('hueco_caliente_min', v)}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <p className='text-xs text-slate-500 mt-3'>
          <Marcador motivo='daily_cold_limit_reached' />
          <Marcador motivo='before_cold_window' />
        </p>
      </Bloque>

      {/* ───────────────────────── 8 · CADA LÍNEA ───────────────────────── */}
      <Bloque
        titulo='8 · Los topes de cada línea'
        descripcion='Se ven aquí para que el cuadro esté completo, pero se cambian en Números de WhatsApp.'
      >
        {/* 🔴 En LECTURA, no editable, y es deliberado.
            Estos campos viven en la ficha de cada número y esa pantalla ya los
            edita. Ponerlos también aquí sería dos sitios escribiendo el mismo
            campo — que es el fallo de `PROTECTED_STATES` otra vez, con otro
            disfraz: dos mandos para lo mismo y gana el que nadie mira.
            Esconderlos tampoco vale: retienen, y una regla que retiene y no se
            ve se acaba buscando en el sitio equivocado. */}
        {lineas.length === 0 ? (
          <p className='text-xs text-slate-400'>Sin líneas.</p>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm min-w-[520px]'>
              <thead>
                <tr className='text-xs uppercase tracking-wide text-slate-400 text-left'>
                  <th className='pb-2 font-medium'>Línea</th>
                  <th className='pb-2 font-medium'>Estado</th>
                  <th className='pb-2 font-medium'>Día</th>
                  <th className='pb-2 font-medium'>Hora</th>
                  <th className='pb-2 font-medium'>Hueco</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {lineas.map((l) => (
                  <tr key={l.phone_id}>
                    <td className='py-2 text-slate-800'>
                      {l.name || l.phone_id}
                      <span className='block text-xs text-slate-400'>{l.phone_number}</span>
                    </td>
                    <td className='py-2'>
                      {l.status === 'active' ? (
                        <span className='text-slate-500'>activa</span>
                      ) : (
                        <span className='text-amber-700 font-medium'>{l.status}</span>
                      )}
                    </td>
                    <td className='py-2 tabular-nums text-slate-600'>{l.daily_cold_limit ?? '—'}</td>
                    <td className='py-2 tabular-nums text-slate-600'>{l.hourly_cold_limit ?? '—'}</td>
                    <td className='py-2 tabular-nums text-slate-600'>
                      {l.min_gap_minutes != null ? l.min_gap_minutes + ' min' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className='text-xs text-slate-500 mt-3'>
          <Marcador motivo='hourly_cold_limit_reached' />
          <Marcador motivo='min_gap_not_met' />
          <Marcador motivo='phone_not_active' />
        </p>
      </Bloque>

      {/* ───────────────────────── 9 · LOS INVARIANTES ───────────────────────── */}
      {/* 🔴 Se ven, con la misma letra que todo lo demás, pero SIN casilla.
          La distinción costó llegar a ella y es el corazón de este rediseño: la
          queja era que estas reglas no se VEÍAN, no que no se pudieran cambiar.
          Son cosas distintas. Apagar cualquiera de éstas es siempre un error en
          cualquier escenario que se nos ocurra, y un interruptor que nunca hay
          que mover no es un mando: es una trampa esperando a las once de la
          noche. Pero esconderlas tampoco vale — retienen, y una regla que
          retiene sin verse se acaba buscando en el sitio equivocado. */}
      <Bloque
        titulo='9 · Lo que no se cambia, y por qué'
        descripcion='También deciden. Están aquí para que esta pantalla sea el cuadro completo, no para tocarlas.'
      >
        <dl className='space-y-3'>
          {INVARIANTES.map((i) => (
            <div key={i.id}>
              <dt className='text-sm font-medium text-slate-800'>{i.titulo}</dt>
              <dd className='text-xs text-slate-500'>
                {i.explica} <span className='text-slate-400'>{i.porque}</span>
              </dd>
            </div>
          ))}
        </dl>
      </Bloque>

      <div className='px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-4'>
        <p className='text-xs text-slate-500'>
          {config?.updated_by
            ? `Última vez: ${config.updated_by}`
            : 'Todo son valores de arranque: nadie ha tocado nada todavía.'}
        </p>
        <div className='flex items-center gap-3'>
          {error && <span className='text-sm text-red-700'>{error}</span>}
          {guardado && (
            <span className='inline-flex items-center gap-1 text-sm text-green-700'>
              <CheckIcon className='w-4 h-4' />
              Guardado
            </span>
          )}
          <button
            type='button'
            onClick={guardar}
            disabled={guardando}
            className='rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50'
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReglasDelPolicia;
